import type { TFunction } from 'i18next';

import type { Locale } from '@/utils/localeFormatters';

import { ENV_OVERLAYS, type EnvOverlay, type FeedWeightOverlays } from './feedWeightOverlays';

/**
 * Overlays the planning system never supplies (`feeding-dashboard-environment.ts` reads
 * only its temperature and saturation), so their absence has no fallback to wait for.
 */
const SENSOR_ONLY: readonly EnvOverlay[] = ['oxygen', 'salinity'];

export interface OverlayCaptionInput {
    overlays: Pick<FeedWeightOverlays, 'missing'>;
    /** The configured planning system's name, or its generic label. */
    systemName: string;
    /** The UI language, which also joins the overlay list. */
    locale: Locale;
}

/**
 * Labels as one list, joined the way the UI language joins a list ("A, B og C"), so every
 * caption under the Feed & Weight chart joins its lists alike.
 */
export function joinLabels(labels: string[], locale: Locale): string {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(labels);
}

/**
 * The overlay names as one list. The list sits after a colon, so the legend labels with
 * their units ("Salinitet ‰") never have to fit into the sentence's grammar.
 */
function overlayList(overlays: EnvOverlay[], locale: Locale, t: TFunction): string {
    return joinLabels(
        overlays.map((overlay) => t(`feedingModule.charts.${overlay}`)),
        locale
    );
}

function missingCaption(input: OverlayCaptionInput, t: TFunction): string | null {
    const { missing } = input.overlays;
    if (missing.length === 0) return null;
    // Nothing is offered at all: the one sentence that has always said so.
    if (missing.length === ENV_OVERLAYS.length) return t('feedingModule.charts.noOverlays');
    const listed = t('feedingModule.charts.overlaysMissing', {
        overlays: overlayList(missing, input.locale, t),
    });
    if (!missing.some((overlay) => SENSOR_ONLY.includes(overlay))) return listed;
    const source = t('feedingModule.charts.overlaysSensorSource', { system: input.systemName });
    return `${listed} ${source}`;
}

/**
 * The captions under the Feed & Weight chart about its environmental overlays: the ones
 * with no reading, which are not offered, so toggles offered on one period do not simply
 * vanish on the next. An overlay with readings on only some days gets no caption (U15):
 * the overlays are off by default, and the gaps show once one is switched on.
 */
export function overlayCaptions(input: OverlayCaptionInput, t: TFunction): string[] {
    const missing = missingCaption(input, t);
    return missing === null ? [] : [missing];
}
